import { Combobox, createListCollection as createComboboxCollection } from '@ark-ui/react/combobox';
import { Portal } from '@ark-ui/react/portal';
import { Select as ArkSelect, createListCollection as createSelectCollection } from '@ark-ui/react/select';
import { css, cva } from '@styled-system/css';
import { Check, ChevronDown, Search } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from './Spinner';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
  /** When true, renders a non-selectable horizontal rule in place of the item. */
  divider?: boolean;
  /** Optional icon rendered before the label (in both the trigger and items). */
  icon?: ReactNode;
}

export type SelectSize = 'sm' | 'md' | 'lg';
export type SelectState = 'default' | 'error';

export interface SelectProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  state?: SelectState;
  size?: SelectSize;
  loading?: boolean;
  searchable?: boolean;
  /** Width of the trigger. Defaults to 100%. */
  width?: string;
  /** When true, popup width matches the trigger. Defaults to true (matches AntD's default). */
  popupMatchTriggerWidth?: boolean;
  /** Optional leading slot rendered inside the trigger before the value text. */
  leading?: ReactNode;
  id?: string;
  'aria-label'?: string;
}

const trigger = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    width: 'full',
    bg: 'surface.default',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.strong',
    borderRadius: 'md',
    color: 'text.primary',
    fontFamily: 'sans',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 120ms ease, box-shadow 120ms ease, background 120ms ease',
    _focusVisible: {
      outline: 'none',
      borderColor: 'border.focus',
      boxShadow: '0 0 0 3px token(colors.brand.primarySoft)',
    },
    '&[data-state="open"]': {
      borderColor: 'border.focus',
      boxShadow: '0 0 0 3px token(colors.brand.primarySoft)',
    },
  },
  variants: {
    size: {
      sm: { h: '26px', paddingX: '5', fontSize: 'caption' },
      md: { h: '32px', paddingX: '6', fontSize: 'bodySm' },
      lg: { h: '38px', paddingX: '7', fontSize: 'body' },
    },
    state: {
      default: {},
      error: {
        borderColor: 'error.default',
        _focusVisible: {
          borderColor: 'error.default',
          boxShadow: '0 0 0 3px token(colors.error.soft)',
        },
        '&[data-state="open"]': {
          borderColor: 'error.default',
          boxShadow: '0 0 0 3px token(colors.error.soft)',
        },
      },
    },
    disabled: {
      true: {
        bg: 'surface.sunken',
        color: 'text.tertiary',
        cursor: 'not-allowed',
        opacity: 0.7,
        _focusVisible: { borderColor: 'border.strong', boxShadow: 'none' },
      },
      false: {},
    },
  },
  defaultVariants: { size: 'md', state: 'default', disabled: false },
});

const valueText = css({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '&[data-placeholder]': { color: 'text.tertiary' },
});

const indicator = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
  transition: 'transform 160ms ease',
  '&[data-state="open"]': { transform: 'rotate(180deg)' },
});

const popupSurface = css({
  bg: 'surface.elevated',
  color: 'text.primary',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  boxShadow: 'lg',
  paddingY: '3',
  fontFamily: 'sans',
  fontSize: 'bodySm',
  zIndex: 1300,
  outline: 'none',
  maxHeight: '320px',
  overflowY: 'auto',
  minWidth: '120px',
});

const itemStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  paddingY: '3',
  paddingX: '5',
  cursor: 'pointer',
  color: 'text.primary',
  fontSize: 'bodySm',
  outline: 'none',
  transition: 'background 80ms ease, color 80ms ease',
  '&[data-highlighted]': { bg: 'surface.sunken' },
  '&[data-state="checked"]': { color: 'brand.primary', fontWeight: 'medium' },
  '&[data-disabled]': { color: 'text.tertiary', cursor: 'not-allowed' },
});

const itemText = css({ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' });

const itemIndicator = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'brand.primary',
  flexShrink: 0,
});

const dividerLine = css({
  height: '1px',
  bg: 'border.subtle',
  marginY: '2',
  marginX: '4',
});

const optionIconSlot = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: 'text.secondary',
});

const searchRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  paddingY: '3',
  paddingX: '5',
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.subtle',
  marginBottom: '2',
  color: 'text.tertiary',
});

const searchInput = css({
  flex: 1,
  minWidth: 0,
  bg: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'text.primary',
  fontFamily: 'sans',
  fontSize: 'bodySm',
  _placeholder: { color: 'text.tertiary' },
});

const emptyState = css({
  paddingY: '5',
  paddingX: '5',
  color: 'text.tertiary',
  fontSize: 'caption',
  textAlign: 'center',
});

const loadingState = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingY: '5',
  gap: '3',
  color: 'text.tertiary',
  fontSize: 'caption',
});

// ============================================================================
// Simple (non-searchable) Select — Ark Select primitive
// ============================================================================

const SimpleSelect = (props: SelectProps) => {
  const { t } = useTranslation();
  const {
    value,
    onChange,
    options,
    placeholder,
    disabled,
    state = 'default',
    size = 'md',
    loading,
    width,
    popupMatchTriggerWidth = true,
    leading,
    id,
    'aria-label': ariaLabel,
  } = props;

  // Strip dividers — they're presentational only and shouldn't be part of the
  // selectable collection (otherwise Ark thinks they have a value and they
  // confuse keyboard navigation).
  const selectableOptions = useMemo(() => options.filter((o) => !o.divider), [options]);

  const collection = useMemo(
    () =>
      createSelectCollection({
        items: selectableOptions,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
        isItemDisabled: (item) => !!item.disabled,
      }),
    [selectableOptions],
  );

  const selectedValue = value ? [value] : [];
  const selectedOption = options.find((o) => o.value === value);

  return (
    <ArkSelect.Root
      collection={collection}
      value={selectedValue}
      onValueChange={(details) => {
        const next = details.value[0];
        if (next != null) onChange(next);
      }}
      disabled={disabled}
      positioning={popupMatchTriggerWidth ? { sameWidth: true } : { sameWidth: false }}
    >
      <ArkSelect.Control style={width ? { width } : undefined}>
        <ArkSelect.Trigger
          id={id}
          aria-label={ariaLabel}
          className={trigger({ size, state, disabled: disabled ? true : false })}
        >
          {leading && <span className={css({ display: 'inline-flex', flexShrink: 0, color: 'text.tertiary' })}>{leading}</span>}
          {selectedOption?.icon && (
            <span className={optionIconSlot}>{selectedOption.icon}</span>
          )}
          <ArkSelect.ValueText className={valueText} placeholder={placeholder ?? t('ui.selectPlaceholder')}>
            {selectedOption?.label}
          </ArkSelect.ValueText>
          <ArkSelect.Indicator className={indicator}>
            <ChevronDown size={14} strokeWidth={1.75} />
          </ArkSelect.Indicator>
        </ArkSelect.Trigger>
      </ArkSelect.Control>
      <Portal>
        <ArkSelect.Positioner>
          <ArkSelect.Content className={popupSurface}>
            {loading ? (
              <div className={loadingState}>
                <Spinner size={14} /> {t('common.loading')}
              </div>
            ) : selectableOptions.length === 0 ? (
              <div className={emptyState}>{t('ui.noOptions')}</div>
            ) : (
              options.map((opt, idx) =>
                opt.divider ? (
                  <div key={`__div-${idx}`} className={dividerLine} aria-hidden />
                ) : (
                  <ArkSelect.Item key={opt.value} item={opt} className={itemStyles}>
                    {opt.icon && <span className={optionIconSlot}>{opt.icon}</span>}
                    <ArkSelect.ItemText className={itemText}>{opt.label}</ArkSelect.ItemText>
                    <ArkSelect.ItemIndicator className={itemIndicator}>
                      <Check size={14} strokeWidth={2} />
                    </ArkSelect.ItemIndicator>
                  </ArkSelect.Item>
                ),
              )
            )}
          </ArkSelect.Content>
        </ArkSelect.Positioner>
      </Portal>
    </ArkSelect.Root>
  );
};

// ============================================================================
// Searchable Select — Ark Combobox primitive
// ============================================================================

const SearchableSelect = (props: SelectProps) => {
  const { t } = useTranslation();
  const {
    value,
    onChange,
    options,
    placeholder,
    disabled,
    state = 'default',
    size = 'md',
    loading,
    width,
    popupMatchTriggerWidth = true,
    leading,
    id,
    'aria-label': ariaLabel,
  } = props;

  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const collection = useMemo(
    () =>
      createComboboxCollection({
        items: filteredOptions,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
        isItemDisabled: (item) => !!item.disabled,
      }),
    [filteredOptions],
  );

  const selectedValue = value ? [value] : [];
  const selectedOption = options.find((o) => o.value === value);

  return (
    <Combobox.Root
      collection={collection}
      value={selectedValue}
      inputValue={query}
      onInputValueChange={(details) => setQuery(details.inputValue)}
      onValueChange={(details) => {
        const next = details.value[0];
        if (next != null) onChange(next);
        setQuery('');
      }}
      onOpenChange={() => {
        // Always reset the search query whether opening or closing — never carry
        // over the previously typed text or auto-fill with the selected label.
        setQuery('');
      }}
      disabled={disabled}
      openOnClick
      selectionBehavior="clear"
      positioning={popupMatchTriggerWidth ? { sameWidth: true } : { sameWidth: false }}
    >
      <Combobox.Control style={width ? { width } : undefined}>
        <Combobox.Trigger
          id={id}
          aria-label={ariaLabel}
          className={trigger({ size, state, disabled: disabled ? true : false })}
          type="button"
        >
          {leading && <span className={css({ display: 'inline-flex', flexShrink: 0, color: 'text.tertiary' })}>{leading}</span>}
          <span
            className={valueText}
            data-placeholder={selectedOption ? undefined : ''}
          >
            {selectedOption?.label ?? placeholder ?? t('ui.selectPlaceholder')}
          </span>
          {loading ? (
            <Spinner size={14} />
          ) : (
            <span className={indicator}>
              <ChevronDown size={14} strokeWidth={1.75} />
            </span>
          )}
        </Combobox.Trigger>
      </Combobox.Control>
      <Portal>
        <Combobox.Positioner>
          <Combobox.Content className={popupSurface}>
            <div className={searchRow}>
              <Search size={14} strokeWidth={1.75} />
              <Combobox.Input className={searchInput} placeholder={t('ui.searchPlaceholder')} autoFocus />
            </div>
            {loading ? (
              <div className={loadingState}>
                <Spinner size={14} /> {t('common.loading')}
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className={emptyState}>{t('ui.noResults')}</div>
            ) : (
              filteredOptions.map((opt) => (
                <Combobox.Item key={opt.value} item={opt} className={itemStyles}>
                  <Combobox.ItemText className={itemText}>{opt.label}</Combobox.ItemText>
                  <Combobox.ItemIndicator className={itemIndicator}>
                    <Check size={14} strokeWidth={2} />
                  </Combobox.ItemIndicator>
                </Combobox.Item>
              ))
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};

// ============================================================================
// Public Select — auto-routes to Combobox or Select based on `searchable`
// ============================================================================

export const Select = (props: SelectProps) => {
  return props.searchable ? <SearchableSelect {...props} /> : <SimpleSelect {...props} />;
};

// ============================================================================
// MultiSelect — Ark Select primitive with multiple selection
// ============================================================================

export interface MultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  state?: SelectState;
  size?: SelectSize;
  width?: string;
  popupMatchTriggerWidth?: boolean;
  leading?: ReactNode;
  /** Affiche une barre de recherche en tête du popup (filtrage client). */
  searchable?: boolean;
  id?: string;
  'aria-label'?: string;
}

export const MultiSelect = (props: MultiSelectProps) => {
  const { t } = useTranslation();
  const {
    value,
    onChange,
    options,
    placeholder,
    disabled,
    state = 'default',
    size = 'md',
    width,
    popupMatchTriggerWidth = true,
    leading,
    searchable = false,
    id,
    'aria-label': ariaLabel,
  } = props;

  const [query, setQuery] = useState('');

  const collection = useMemo(
    () =>
      createSelectCollection({
        items: options,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
        isItemDisabled: (item) => !!item.disabled,
      }),
    [options],
  );

  // Filtrage d'affichage uniquement — la sélection (Ark) garde toutes les
  // options, on masque juste les lignes qui ne matchent pas la recherche.
  const visibleOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const selectedLabels = value
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter((l): l is string => !!l);

  return (
    <ArkSelect.Root
      multiple
      collection={collection}
      value={value}
      onValueChange={(details) => onChange(details.value)}
      onOpenChange={() => setQuery('')}
      disabled={disabled}
      positioning={popupMatchTriggerWidth ? { sameWidth: true } : { sameWidth: false }}
      closeOnSelect={false}
    >
      <ArkSelect.Control style={width ? { width } : undefined}>
        <ArkSelect.Trigger
          id={id}
          aria-label={ariaLabel}
          className={trigger({ size, state, disabled: disabled ? true : false })}
        >
          {leading && <span className={css({ display: 'inline-flex', flexShrink: 0, color: 'text.tertiary' })}>{leading}</span>}
          <ArkSelect.ValueText className={valueText} placeholder={placeholder ?? t('ui.selectPlaceholder')}>
            {selectedLabels.join(', ')}
          </ArkSelect.ValueText>
          <ArkSelect.Indicator className={indicator}>
            <ChevronDown size={14} strokeWidth={1.75} />
          </ArkSelect.Indicator>
        </ArkSelect.Trigger>
      </ArkSelect.Control>
      <Portal>
        <ArkSelect.Positioner>
          <ArkSelect.Content className={popupSurface}>
            {searchable && (
              <div className={searchRow}>
                <Search size={14} strokeWidth={1.75} />
                <input
                  className={searchInput}
                  placeholder={t('ui.searchPlaceholder')}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  // Bloque le typeahead / la navigation d'Ark pendant la saisie
                  // (Escape continue de fermer le popup).
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') event.stopPropagation();
                  }}
                  autoFocus
                />
              </div>
            )}
            {options.length === 0 ? (
              <div className={emptyState}>{t('ui.noOptions')}</div>
            ) : visibleOptions.length === 0 ? (
              <div className={emptyState}>{t('ui.noResults')}</div>
            ) : (
              visibleOptions.map((opt) => (
                <ArkSelect.Item key={opt.value} item={opt} className={itemStyles}>
                  {opt.icon && <span className={optionIconSlot}>{opt.icon}</span>}
                  <ArkSelect.ItemText className={itemText}>{opt.label}</ArkSelect.ItemText>
                  <ArkSelect.ItemIndicator className={itemIndicator}>
                    <Check size={14} strokeWidth={2} />
                  </ArkSelect.ItemIndicator>
                </ArkSelect.Item>
              ))
            )}
          </ArkSelect.Content>
        </ArkSelect.Positioner>
      </Portal>
    </ArkSelect.Root>
  );
};
